import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import StarIcon from "@mui/icons-material/Star";
import GroupsIcon from "@mui/icons-material/Groups";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import EuroIcon from "@mui/icons-material/Euro";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../store";
import { getTournoi } from "../../features/tournoi/tournoiSlice";
import { resolveTournoiHome } from "../../utils/resolveTournoiHome";

function highlightIcon(iconKey: string | undefined, sx: object) {
  switch (iconKey) {
    case "marraine":
      return <AutoAwesomeIcon sx={sx} />;
    case "ateliers":
      return <SportsTennisIcon sx={sx} />;
    case "goodies":
      return <CardGiftcardIcon sx={sx} />;
    case "tarifs":
      return <EuroIcon sx={sx} />;
    case "convivialite":
      return <LocalBarIcon sx={sx} />;
    case "demiJournee":
      return <ScheduleIcon sx={sx} />;
    default:
      return <StarIcon sx={sx} />;
  }
}

export type HomeContentSectionProps = {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
};

/** En-tête de section réutilisable (cohérence visuelle entre les blocs de la Home). */
export const HomeContentSection: React.FC<HomeContentSectionProps> = ({
  eyebrow,
  title,
  subtitle,
  children,
}) => {
  const theme = useTheme();
  return (
    <Box
      component="section"
      sx={{
        mb: { xs: 5, md: 7 },
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: 640,
          mx: "auto",
          px: 1,
          mb: { xs: 2.5, sm: 3.5 },
        }}
      >
        <Typography
          variant="overline"
          sx={{
            display: "block",
            color:
              theme.palette.mode === "dark"
                ? theme.palette.secondary.light
                : theme.palette.primary.main,
            fontWeight: 700,
            letterSpacing: "0.14em",
            fontSize: "0.7rem",
            mb: 0.75,
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: 700,
            letterSpacing: "-0.02em",
            fontSize: { xs: "1.35rem", sm: "1.5rem" },
            color:
              theme.palette.mode === "dark" ? "grey.100" : "text.primary",
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="body2"
            sx={(theme) => ({
              mt: 1.25,
              lineHeight: 1.65,
              maxWidth: 520,
              mx: "auto",
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.text.secondary,
            })}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {children}
    </Box>
  );
};

export const HomeGallery: React.FC = () => {
  const tournoi = useAppSelector(getTournoi);
  const galleryUrls = resolveTournoiHome(tournoi).galleryUrls;
  const img0 = galleryUrls[0] ?? "/home-gallery/home-gallery-salle.png";
  const img1 = galleryUrls[1] ?? galleryUrls[0] ?? img0;
  const img2 = galleryUrls[2] ?? galleryUrls[1] ?? img0;

  return (
    <HomeContentSection
      eyebrow="Ambiance"
      title="Le tournoi en images"
      subtitle="Salle, convivialité et esprit compétition — le rendez-vous national SQY Ping."
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <Box
            component="img"
            src={img0}
            alt="Salle du tournoi SQY Ping"
            sx={{
              width: "100%",
              height: { xs: 240, sm: 320, md: 400 },
              objectFit: "cover",
              borderRadius: 2,
              boxShadow: (t) =>
                t.palette.mode === "dark" ? 8 : "0 8px 28px rgba(0,0,0,0.1)",
            }}
            loading="lazy"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack spacing={2.5}>
            <Box
              component="img"
              src={img1}
              alt="Ambiance tournoi SQY Ping"
              sx={{
                width: "100%",
                height: { xs: 180, sm: 240, md: 192 },
                objectFit: "cover",
                borderRadius: 2,
                boxShadow: (t) =>
                  t.palette.mode === "dark" ? 6 : "0 6px 20px rgba(0,0,0,0.08)",
              }}
              loading="lazy"
            />
            <Box
              component="img"
              src={img2}
              alt="Tournoi SQY Ping"
              sx={{
                width: "100%",
                height: { xs: 180, sm: 240, md: 192 },
                objectFit: "cover",
                borderRadius: 2,
                boxShadow: (t) =>
                  t.palette.mode === "dark" ? 6 : "0 6px 20px rgba(0,0,0,0.08)",
              }}
              loading="lazy"
            />
          </Stack>
        </Grid>
      </Grid>
    </HomeContentSection>
  );
};

function useHomeMarketingCardStyles() {
  const theme = useTheme();
  const iconAccentSx = {
    fontSize: 40,
    mb: 1,
    color:
      theme.palette.mode === "dark"
        ? theme.palette.secondary.light
        : theme.palette.primary.main,
  };
  const iconSecondarySx = {
    fontSize: 40,
    mb: 1,
    color:
      theme.palette.mode === "dark"
        ? theme.palette.secondary.light
        : theme.palette.secondary.main,
  };
  const cardBodySx = {
    color:
      theme.palette.mode === "dark"
        ? theme.palette.grey[300]
        : theme.palette.text.secondary,
  };
  const cardSx = {
    height: "100%" as const,
    borderColor: theme.palette.divider,
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(0,0,0,0.06)",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow:
        theme.palette.mode === "dark"
          ? "0 6px 20px rgba(0,0,0,0.45)"
          : "0 8px 24px rgba(0,0,0,0.1)",
      transform: "translateY(-2px)",
    },
  };
  return { theme, iconAccentSx, iconSecondarySx, cardBodySx, cardSx };
}

/**
 * Points marketing optionnels (ex. JN Féminisation).
 * Rendu uniquement si `tournoi.home.highlights` est renseigné — site A inchangé.
 */
export const HomeHighlights: React.FC = () => {
  const tournoi = useAppSelector(getTournoi);
  const home = resolveTournoiHome(tournoi);
  const { iconAccentSx, iconSecondarySx, cardBodySx, cardSx } =
    useHomeMarketingCardStyles();

  if (home.highlights.length === 0) return null;

  const [featured, ...rest] = home.highlights;
  const featuredIsMarraine = featured.icon === "marraine";

  return (
    <HomeContentSection
      eyebrow={home.highlightsEyebrow}
      title={home.highlightsTitle}
      subtitle={home.highlightsSubtitle}
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{
              ...cardSx,
              borderColor: (t) =>
                alpha(
                  featuredIsMarraine
                    ? t.palette.secondary.main
                    : t.palette.primary.main,
                  t.palette.mode === "dark" ? 0.45 : 0.28
                ),
              background: (t) =>
                `linear-gradient(
                  135deg,
                  ${alpha(
                    featuredIsMarraine
                      ? t.palette.secondary.main
                      : t.palette.primary.main,
                    t.palette.mode === "dark" ? 0.16 : 0.08
                  )} 0%,
                  ${t.palette.background.paper} 62%
                )`,
            }}
          >
            <CardContent
              sx={{
                pt: { xs: 2.5, sm: 3 },
                pb: { xs: 2.5, sm: 3 },
                px: { xs: 2.5, sm: 3.5 },
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1.5, sm: 2.5 }}
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                {highlightIcon(
                  featured.icon,
                  {
                    ...iconAccentSx,
                    fontSize: 48,
                    mb: 0,
                  }
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      display: "block",
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      color: (t) =>
                        t.palette.mode === "dark"
                          ? t.palette.secondary.light
                          : t.palette.secondary.dark,
                      mb: 0.5,
                    }}
                  >
                    À la une
                  </Typography>
                  <Typography
                    variant="h6"
                    component="h3"
                    fontWeight={700}
                    gutterBottom
                    sx={{ letterSpacing: "-0.02em" }}
                  >
                    {featured.title}
                  </Typography>
                  <Typography variant="body1" sx={{ ...cardBodySx, maxWidth: 640 }}>
                    {featured.body}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        {rest.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} key={`${item.title}-${index}`}>
            <Card variant="outlined" sx={cardSx}>
              <CardContent sx={{ pt: 2.5, pb: 2 }}>
                {highlightIcon(
                  item.icon,
                  index % 2 === 0 ? iconAccentSx : iconSecondarySx
                )}
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={cardBodySx}>
                  {item.body}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </HomeContentSection>
  );
};

export const HomeWhyParticipate: React.FC = () => {
  const tournoi = useAppSelector(getTournoi);
  const home = resolveTournoiHome(tournoi);
  const { iconAccentSx, iconSecondarySx, cardBodySx, cardSx } =
    useHomeMarketingCardStyles();

  return (
    <HomeContentSection
      eyebrow="Nos atouts"
      title="Pourquoi participer ?"
      subtitle="Une expérience pensée pour les joueuses et les joueurs, du loisir au haut niveau."
    >
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={cardSx}>
            <CardContent sx={{ pt: 2.5, pb: 2 }}>
              <StarIcon sx={iconAccentSx} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Organisation premium
              </Typography>
              <Typography variant="body2" sx={cardBodySx}>
                {home.whyParticipateOrganization}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={cardSx}>
            <CardContent sx={{ pt: 2.5, pb: 2 }}>
              <GroupsIcon sx={iconSecondarySx} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Pour tous les niveaux
              </Typography>
              <Typography variant="body2" sx={cardBodySx}>
                {home.whyParticipateLevels}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={cardSx}>
            <CardContent sx={{ pt: 2.5, pb: 2 }}>
              <LocalBarIcon sx={iconAccentSx} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Convivialité
              </Typography>
              <Typography variant="body2" sx={cardBodySx}>
                Buvette sur place et accueil chaleureux pour une journée
                agréable.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </HomeContentSection>
  );
};

/** Rappel discret vers le règlement (sans afficher de grille tarifaire). */
export const HomeReglementCta: React.FC = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        textAlign: "center",
        mb: { xs: 4, md: 5 },
        px: 1,
      }}
    >
      <Button
        component={Link}
        to="/rules"
        variant="outlined"
        color="primary"
        size="large"
        sx={{
          fontWeight: 600,
          px: 3,
          borderWidth: 2,
          "&:hover": { borderWidth: 2 },
          ...(theme.palette.mode === "dark" && {
            borderColor: theme.palette.secondary.light,
            color: theme.palette.secondary.light,
            "&:hover": {
              borderColor: theme.palette.secondary.light,
              color: theme.palette.secondary.light,
              backgroundColor: alpha(theme.palette.secondary.light, 0.14),
            },
          }),
        }}
      >
        Règlement & modalités
      </Button>
      <Typography
        variant="caption"
        display="block"
        sx={{
          mt: 1.5,
          maxWidth: 400,
          mx: "auto",
          color:
            theme.palette.mode === "dark"
              ? theme.palette.grey[300]
              : theme.palette.text.secondary,
        }}
      >
        Conditions d&apos;inscription et informations officielles sur la page
        règlement.
      </Typography>
    </Box>
  );
};
